import React, { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { sitesApi } from '../../api/client';
import { HtmlEmbedEditModal } from '../HtmlEmbedEditModal';
import { CompeteFormEditModal } from '../CompeteForm/CompeteFormEditModal';
import { MediaUploadModal } from '../MediaUploadModal';
import type {
  ComponentEditModalProps,
  NavbarData,
  HeroData,
  SplitHeroData,
  FeaturesData,
  CtaData,
  Page,
} from './ComponentEditModal.types';
import {
  formatComponentTypeName,
  initializeComponentData,
  clearCustomColors,
} from './ComponentEditModal.utils';
import { conversionPopupDataSchema } from '../../schemas/components/conversion-popup.schemas';
import type { NavigationMenuData } from '../../schemas/validation';
import { useComponentEditDraft } from '../../contexts/ComponentEditDraftContext';
import { ModalFooter, ThemeColorsConfirmModal } from './shared/ModalSubComponents';
import { LinkTargetPageProvider } from './shared/LinkTargetPageContext';
import { NavbarEditor } from './editors/NavbarEditor';
import { NavigationMenuEditor } from './editors/NavigationMenuEditor';
import { FooterEditor } from './editors/FooterEditor';
import { HeroEditor } from './editors/HeroEditor';
import { SplitHeroEditor } from './editors/SplitHeroEditor';
import { HtmlSplitEditor } from './editors/HtmlSplitEditor';
import { SimpleHeaderEditor } from './editors/SimpleHeaderEditor';
import { FeaturesEditor } from './editors/FeaturesEditor';
import { CtaEditor } from './editors/CtaEditor';
import { TitleSubtitleEditor } from './editors/TitleSubtitleEditor';
import { ContactInfoEditor } from './editors/ContactInfoEditor';
import { TextBlocksEditor } from './editors/TextBlocksEditor';
import { FaqEditor } from './editors/FaqEditor';
import { TestimonialsEditor } from './editors/TestimonialsEditor';
import { HeadContentEditor } from './editors/HeadContentEditor';
import { BodyEndEditor } from './editors/BodyEndEditor';
import { ContentAreaEditor } from './editors/ContentAreaEditor';
import { TeamEditor } from './editors/TeamEditor';
import { MediaTextEditor } from './editors/MediaTextEditor';
import { PricingEditor } from './editors/PricingEditor';
import { FormEditor } from './editors/FormEditor';
import { FormSplitEditor } from './editors/FormSplitEditor';
import { VideoEditor } from './editors/VideoEditor';
import { CountdownTimerEditor } from './editors/CountdownTimerEditor';
import { ImageGalleryEditor } from './editors/ImageGalleryEditor';
import { ImageInfoEditor } from './editors/ImageInfoEditor';
import { ImageCardEditor } from './editors/ImageCardEditor';
import { PromoBannerEditor } from './editors/PromoBannerEditor';
import { LogocloudEditor } from './editors/LogocloudEditor';
import { StatsEditor } from './editors/StatsEditor';
import { BlogPreviewEditor } from './editors/BlogPreviewEditor';
import { ConversionPopupEditor } from './editors/ConversionPopupEditor';

export const ComponentEditModal: React.FC<ComponentEditModalProps> = ({
  isOpen,
  component,
  onClose,
  onSave,
  isLoading = false,
  siteId,
  currentPageId,
  currentPageSlug,
  layoutLinkHeaderComponents,
  layoutLinkFooterComponents,
  currentPageComponents,
}) => {
  // All hooks must be called before any conditional returns
  const [formData, setFormData] = useState<any>({});
  const [pages, setPages] = useState<Page[]>([]);
  const [isLoadingPages, setIsLoadingPages] = useState(false);
  const [isLogoModalOpen, setIsLogoModalOpen] = useState(false);
  const [showThemeColorsConfirm, setShowThemeColorsConfirm] = useState(false);
  // Media upload modal state
  const [isMediaUploadModalOpen, setIsMediaUploadModalOpen] = useState(false);
  const [currentMediaType, setCurrentMediaType] = useState<'image' | 'video'>('image');
  const [currentAssetType, setCurrentAssetType] = useState<string>('image');
  const [currentItemIndex, setCurrentItemIndex] = useState<number | undefined>(undefined);
  const [conversionPopupValidationError, setConversionPopupValidationError] = useState<
    string | null
  >(null);
  const editDraftCtx = useComponentEditDraft();

  // Helper to handle media upload callbacks with proper typing
  const handleOpenMediaUpload = useCallback(
    (mediaType: 'image' | 'video', assetType: string, itemIndex?: number) => {
      setCurrentMediaType(mediaType);
      setCurrentAssetType(assetType);
      setCurrentItemIndex(itemIndex);
      setIsMediaUploadModalOpen(true);
    },
    []
  );

  // Handle when media is uploaded
  const handleMediaUploaded = useCallback(
    (
      mediaUrl: string,
      cloudfrontUrl?: string,
      mediaId?: string,
      metadata?: {
        mimeType: string;
        duration?: number;
        width?: number;
        height?: number;
        fileSize: number;
      },
      s3Key?: string
    ) => {
      if (!component) return;

      // Update form data based on component type and asset type
      const updateMediaInFormData = (updates: any) => {
        setFormData((prev: any) => ({ ...prev, ...updates }));
      };

      // Handle different component types
      switch (component.type) {
        case 'hero':
        case 'cta':
        case 'countdown-timer':
          if (currentAssetType === 'background') {
            // Background image is a nested object with enabled flag
            updateMediaInFormData({
              backgroundImage: {
                ...formData.backgroundImage,
                enabled: true,
                url: mediaUrl,
                cloudfrontUrl: cloudfrontUrl,
                mediaId: mediaId,
              },
            });
          } else if (currentAssetType === 'image') {
            updateMediaInFormData({
              imageUrl: cloudfrontUrl || mediaUrl,
              imageId: mediaId,
            });
          }
          break;

        case 'split-hero':
          // Split-hero uses a nested image object
          if (currentAssetType === 'image') {
            updateMediaInFormData({
              image: {
                ...formData.image,
                enabled: true,
                url: mediaUrl,
                cloudfrontUrl: cloudfrontUrl,
                mediaId: mediaId,
              },
            });
          }
          break;

        case 'media-text':
          if (currentAssetType === 'thumbnail') {
            updateMediaInFormData({
              media: {
                ...formData.media,
                thumbnail: {
                  ...formData.media?.thumbnail,
                  url: mediaUrl,
                  cloudfrontUrl: cloudfrontUrl,
                  mediaId: mediaId,
                },
              },
            });
          } else {
            updateMediaInFormData({
              media: {
                ...formData.media,
                type: currentMediaType,
                url: mediaUrl,
                cloudfrontUrl: cloudfrontUrl,
                mediaId: mediaId,
                s3Key: s3Key,
                ...(currentMediaType === 'video'
                  ? { sourceType: 'upload' as const, embedUrl: undefined }
                  : {}),
              },
            });
          }
          break;

        case 'form-split':
          if (currentAssetType === 'thumbnail') {
            updateMediaInFormData({
              media: {
                ...formData.media,
                thumbnail: {
                  ...formData.media?.thumbnail,
                  url: mediaUrl,
                  cloudfrontUrl: cloudfrontUrl,
                  mediaId: mediaId,
                },
              },
            });
          } else {
            updateMediaInFormData({
              media: {
                ...formData.media,
                type: currentMediaType,
                url: mediaUrl,
                cloudfrontUrl: cloudfrontUrl,
                mediaId: mediaId,
                s3Key: s3Key,
                // Uploaded file must use Plyr path, not YouTube/Vimeo embed
                ...(currentMediaType === 'video'
                  ? { sourceType: 'upload' as const, embedUrl: undefined }
                  : {}),
              },
            });
          }
          break;

        case 'video':
          if (currentAssetType === 'video') {
            updateMediaInFormData({
              source: {
                ...formData.source,
                url: mediaUrl,
                cloudfrontUrl: cloudfrontUrl,
                mediaId: mediaId,
                mimeType: metadata?.mimeType,
                duration: metadata?.duration,
              },
            });
          } else if (currentAssetType === 'thumbnail' || currentAssetType === 'poster') {
            updateMediaInFormData({
              thumbnail: {
                ...formData.thumbnail,
                url: mediaUrl,
                cloudfrontUrl: cloudfrontUrl,
                mediaId: mediaId,
              },
            });
          }
          break;

        case 'image-gallery':
          // Update specific item in the gallery array
          if (currentItemIndex !== undefined && formData.items) {
            const updatedItems = [...formData.items];
            updatedItems[currentItemIndex] = {
              ...updatedItems[currentItemIndex],
              imageUrl: mediaUrl,
              cloudfrontUrl: cloudfrontUrl,
              mediaId: mediaId,
            };
            updateMediaInFormData({ items: updatedItems });
          }
          break;

        case 'image-card':
          // Update specific card item in the array
          if (currentItemIndex !== undefined && formData.items) {
            const updatedItems = [...formData.items];
            updatedItems[currentItemIndex] = {
              ...updatedItems[currentItemIndex],
              imageUrl: mediaUrl,
              cloudfrontUrl: cloudfrontUrl,
              mediaId: mediaId,
            };
            updateMediaInFormData({ items: updatedItems });
          }
          break;

        case 'image-info':
          // Update specific info item in the array
          if (currentItemIndex !== undefined && formData.items) {
            const updatedItems = [...formData.items];
            updatedItems[currentItemIndex] = {
              ...updatedItems[currentItemIndex],
              imageUrl: mediaUrl,
              cloudfrontUrl: cloudfrontUrl,
              mediaId: mediaId,
            };
            updateMediaInFormData({ items: updatedItems });
          }
          break;

        case 'features':
          // Update specific feature in the features array
          if (currentItemIndex !== undefined && formData.features) {
            const updatedFeatures = [...formData.features];
            updatedFeatures[currentItemIndex] = {
              ...updatedFeatures[currentItemIndex],
              imageUrl: mediaUrl,
              cloudfrontUrl: cloudfrontUrl,
              mediaId: mediaId,
            };
            updateMediaInFormData({ features: updatedFeatures });
          }
          break;

        case 'conversion-popup':
          if (currentAssetType === 'image') {
            updateMediaInFormData({
              imageUrl: cloudfrontUrl || mediaUrl,
              crop: null,
            });
          }
          break;

        default:
          // Generic update for other components
          if (currentAssetType === 'background') {
            // Background image is a nested object with enabled flag
            updateMediaInFormData({
              backgroundImage: {
                ...formData.backgroundImage,
                enabled: true,
                url: mediaUrl,
                cloudfrontUrl: cloudfrontUrl,
                mediaId: mediaId,
              },
            });
          } else {
            updateMediaInFormData({
              imageUrl: cloudfrontUrl || mediaUrl,
              imageId: mediaId,
            });
          }
      }

      setIsMediaUploadModalOpen(false);
    },
    [component, currentMediaType, currentAssetType, currentItemIndex, formData]
  );

  const loadPages = useCallback(async () => {
    if (!siteId) return;
    try {
      setIsLoadingPages(true);
      const sitePages = await sitesApi.getPages(siteId);
      setPages(sitePages);
    } catch (error) {
      console.error('Failed to load pages:', error);
      setPages([]);
    } finally {
      setIsLoadingPages(false);
    }
  }, [siteId]);

  // Initialize form data when component changes; clear conversion-popup validation on switch
  useEffect(() => {
    setConversionPopupValidationError(null);
    if (!isOpen || !component || component.type === 'html-embed') return;

    const initialData = initializeComponentData(component.type, component.data);
    setFormData(initialData);
  }, [component, isOpen]);

  // Let canvas preview (e.g. conversion popup) reflect unsaved form edits while this modal is open.
  useEffect(() => {
    if (!editDraftCtx) return;
    if (!isOpen || !component) {
      editDraftCtx.setDraft(null);
      return;
    }
    editDraftCtx.setDraft({
      componentId: component.id,
      data: { ...(component.data as Record<string, unknown>), ...formData },
    });
  }, [isOpen, component, formData, editDraftCtx]);

  // Load pages when modal opens
  useEffect(() => {
    if (isOpen && siteId && component?.type !== 'html-embed') {
      loadPages();
    }
  }, [isOpen, siteId, component?.type, loadPages]);

  const handleUseThemeColors = () => {
    setShowThemeColorsConfirm(true);
  };

  const confirmUseThemeColors = useCallback(() => {
    if (!component) return;
    const updatedFormData = clearCustomColors(component.type, formData);
    setFormData(updatedFormData);
    setShowThemeColorsConfirm(false);
  }, [component, formData]);

  const handleSave = useCallback(() => {
    if (!component) return;
    if (component.type === 'conversion-popup') {
      const parsed = conversionPopupDataSchema.safeParse(formData);
      if (!parsed.success) {
        const first = parsed.error.issues[0]?.message ?? 'Please fix the errors before saving.';
        setConversionPopupValidationError(first);
        return;
      }
      setConversionPopupValidationError(null);
      onSave(component.id, parsed.data);
      return;
    }
    setConversionPopupValidationError(null);
    onSave(component.id, formData);
  }, [component, formData, onSave]);

  // Special handling for html-embed component - use dedicated modal
  if (component?.type === 'html-embed') {
    return (
      <HtmlEmbedEditModal
        isOpen={isOpen}
        onClose={onClose}
        onSave={data => onSave(component.id, data)}
        initialData={component.data}
        isLoading={isLoading}
      />
    );
  }

  if (component?.type === 'compete-form') {
    return (
      <CompeteFormEditModal
        isOpen={isOpen}
        siteId={siteId}
        initialData={component.data}
        isLoading={isLoading}
        onClose={onClose}
        onSave={data => onSave(component.id, data)}
      />
    );
  }

  if (!isOpen || !component) return null;

  const renderForm = () => {
    switch (component.type) {
      case 'navbar':
        return (
          <NavbarEditor
            formData={formData as NavbarData}
            setFormData={setFormData}
            pages={pages}
            isLoadingPages={isLoadingPages}
            siteId={siteId}
            isLoading={isLoading}
            isLogoModalOpen={isLogoModalOpen}
            setIsLogoModalOpen={setIsLogoModalOpen}
            currentPageComponents={currentPageComponents}
          />
        );
      case 'navigation-menu':
        return (
          <NavigationMenuEditor
            formData={formData as NavigationMenuData}
            setFormData={setFormData}
            pages={pages}
            isLoadingPages={isLoadingPages}
            siteId={siteId}
            isLoading={isLoading}
            isLogoModalOpen={isLogoModalOpen}
            setIsLogoModalOpen={setIsLogoModalOpen}
            currentPageComponents={currentPageComponents}
          />
        );
      case 'footer':
        return (
          <FooterEditor
            formData={formData}
            setFormData={setFormData}
            pages={pages}
            isLoadingPages={isLoadingPages}
            currentPageComponents={currentPageComponents}
          />
        );
      case 'hero':
        return (
          <HeroEditor
            formData={formData as HeroData}
            setFormData={setFormData}
            pages={pages}
            isLoadingPages={isLoadingPages}
            onOpenMediaUpload={handleOpenMediaUpload}
            currentPageComponents={currentPageComponents}
          />
        );
      case 'split-hero':
        return (
          <SplitHeroEditor
            formData={formData as SplitHeroData}
            setFormData={setFormData}
            pages={pages}
            isLoadingPages={isLoadingPages}
            onOpenMediaUpload={handleOpenMediaUpload}
            currentPageComponents={currentPageComponents}
          />
        );
      case 'html-split':
        return (
          <HtmlSplitEditor
            formData={formData}
            setFormData={setFormData}
            pages={pages}
            isLoadingPages={isLoadingPages}
            currentPageComponents={currentPageComponents}
          />
        );
      case 'simple-header':
        return (
          <SimpleHeaderEditor
            formData={formData}
            setFormData={setFormData}
            pages={pages}
            isLoadingPages={isLoadingPages}
            currentPageComponents={currentPageComponents}
          />
        );
      case 'features':
        return (
          <FeaturesEditor
            formData={formData as FeaturesData}
            setFormData={setFormData}
            pages={pages}
            isLoadingPages={isLoadingPages}
            onOpenMediaUpload={(index: number) => handleOpenMediaUpload('image', 'image', index)}
            currentPageComponents={currentPageComponents}
          />
        );
      case 'cta':
        return (
          <CtaEditor
            formData={formData as CtaData}
            setFormData={setFormData}
            pages={pages}
            isLoadingPages={isLoadingPages}
            onOpenMediaUpload={handleOpenMediaUpload}
            currentPageComponents={currentPageComponents}
          />
        );
      case 'title-subtitle':
        return (
          <TitleSubtitleEditor
            formData={formData}
            setFormData={setFormData}
            pages={pages}
            isLoadingPages={isLoadingPages}
            currentPageComponents={currentPageComponents}
          />
        );
      case 'contact-info':
        return (
          <ContactInfoEditor
            formData={formData}
            setFormData={setFormData}
            pages={pages}
            isLoadingPages={isLoadingPages}
            currentPageComponents={currentPageComponents}
          />
        );
      case 'text-blocks':
        return (
          <TextBlocksEditor
            formData={formData}
            setFormData={setFormData}
            pages={pages}
            isLoadingPages={isLoadingPages}
            currentPageComponents={currentPageComponents}
          />
        );
      case 'faq':
        return (
          <FaqEditor
            formData={formData}
            setFormData={setFormData}
            pages={pages}
            isLoadingPages={isLoadingPages}
            currentPageComponents={currentPageComponents}
          />
        );
      case 'testimonials':
        return (
          <TestimonialsEditor
            formData={formData}
            setFormData={setFormData}
            pages={pages}
            isLoadingPages={isLoadingPages}
            currentPageComponents={currentPageComponents}
            siteId={siteId || ''}
          />
        );
      case 'head-content':
        return (
          <HeadContentEditor
            formData={formData}
            setFormData={setFormData}
            pages={pages}
            isLoadingPages={isLoadingPages}
            currentPageComponents={currentPageComponents}
          />
        );
      case 'body-end':
        return (
          <BodyEndEditor
            formData={formData}
            setFormData={setFormData}
            pages={pages}
            isLoadingPages={isLoadingPages}
            currentPageComponents={currentPageComponents}
          />
        );
      case 'content-area':
        return (
          <ContentAreaEditor
            formData={formData}
            setFormData={setFormData}
            pages={pages}
            isLoadingPages={isLoadingPages}
            currentPageComponents={currentPageComponents}
          />
        );
      case 'team':
        return (
          <TeamEditor
            formData={formData}
            setFormData={setFormData}
            pages={pages}
            isLoadingPages={isLoadingPages}
            siteId={siteId}
            currentPageComponents={currentPageComponents}
          />
        );
      case 'media-text':
        return (
          <MediaTextEditor
            formData={formData}
            setFormData={setFormData}
            pages={pages}
            isLoadingPages={isLoadingPages}
            onOpenMediaUpload={handleOpenMediaUpload}
            currentPageComponents={currentPageComponents}
          />
        );
      case 'pricing':
        return (
          <PricingEditor
            formData={formData}
            setFormData={setFormData}
            pages={pages}
            isLoadingPages={isLoadingPages}
            currentPageComponents={currentPageComponents}
          />
        );
      case 'form':
        return (
          <FormEditor
            formData={formData}
            setFormData={setFormData}
            pages={pages}
            isLoadingPages={isLoadingPages}
            currentPageComponents={currentPageComponents}
          />
        );
      case 'form-split':
        return (
          <FormSplitEditor
            formData={formData}
            setFormData={setFormData}
            pages={pages}
            isLoadingPages={isLoadingPages}
            onOpenMediaUpload={handleOpenMediaUpload}
            currentPageComponents={currentPageComponents}
          />
        );
      case 'video':
        return (
          <VideoEditor
            formData={formData}
            setFormData={setFormData}
            pages={pages}
            isLoadingPages={isLoadingPages}
            onOpenMediaUpload={handleOpenMediaUpload}
            currentPageComponents={currentPageComponents}
          />
        );
      case 'countdown-timer':
        return (
          <CountdownTimerEditor
            formData={formData}
            setFormData={setFormData}
            pages={pages}
            isLoadingPages={isLoadingPages}
            onOpenMediaUpload={handleOpenMediaUpload}
            currentPageComponents={currentPageComponents}
          />
        );
      case 'image-gallery':
        return (
          <ImageGalleryEditor
            formData={formData}
            setFormData={setFormData}
            pages={pages}
            isLoadingPages={isLoadingPages}
            onOpenMediaUpload={handleOpenMediaUpload}
            currentPageComponents={currentPageComponents}
          />
        );
      case 'image-info':
        return (
          <ImageInfoEditor
            formData={formData}
            setFormData={setFormData}
            pages={pages}
            isLoadingPages={isLoadingPages}
            onOpenMediaUpload={handleOpenMediaUpload}
            currentPageComponents={currentPageComponents}
          />
        );
      case 'image-card':
        return (
          <ImageCardEditor
            formData={formData}
            setFormData={setFormData}
            pages={pages}
            isLoadingPages={isLoadingPages}
            onOpenMediaUpload={handleOpenMediaUpload}
            currentPageComponents={currentPageComponents}
          />
        );
      case 'promo-banner':
        return (
          <PromoBannerEditor
            formData={formData}
            setFormData={setFormData}
            pages={pages}
            isLoadingPages={isLoadingPages}
            currentPageComponents={currentPageComponents}
          />
        );
      case 'logocloud':
        return <LogocloudEditor formData={formData} setFormData={setFormData} siteId={siteId} />;
      case 'stats':
        return <StatsEditor formData={formData} setFormData={setFormData} />;
      case 'blog-preview':
        return <BlogPreviewEditor formData={formData} setFormData={setFormData} />;
      case 'conversion-popup':
        return (
          <ConversionPopupEditor
            formData={formData}
            setFormData={setFormData}
            pages={pages}
            isLoadingPages={isLoadingPages}
            currentPageComponents={currentPageComponents}
            onOpenMediaUpload={handleOpenMediaUpload}
          />
        );
      default:
        return <div>Unknown component type: {(component as any).type}</div>;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[70]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Edit {formatComponentTypeName(component.type)} Component
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={24} />
          </button>
        </div>

        {/* Form Content */}
        <LinkTargetPageProvider
          value={{
            currentPageId,
            currentPageSlug,
            layoutLinkHeaderComponents,
            layoutLinkFooterComponents,
          }}
        >
          <div className="flex-1 overflow-y-auto p-6">{renderForm()}</div>
        </LinkTargetPageProvider>

        {conversionPopupValidationError && component.type === 'conversion-popup' ? (
          <div
            role="alert"
            className="px-6 py-3 border-t border-red-200 bg-red-50 text-sm text-red-800 shrink-0"
          >
            {conversionPopupValidationError}
          </div>
        ) : null}

        {/* Footer */}
        <ModalFooter
          onUseThemeColors={handleUseThemeColors}
          onCancel={onClose}
          onSave={handleSave}
          isLoading={isLoading}
        />
      </div>

      {/* Theme Colors Confirmation Modal */}
      {showThemeColorsConfirm && (
        <ThemeColorsConfirmModal
          onConfirm={confirmUseThemeColors}
          onCancel={() => setShowThemeColorsConfirm(false)}
        />
      )}

      {/* Media Upload Modal */}
      <MediaUploadModal
        isOpen={isMediaUploadModalOpen}
        onClose={() => setIsMediaUploadModalOpen(false)}
        siteId={siteId || ''}
        mediaType={currentMediaType}
        componentType={
          // Use undefined for invisible component types (defaults to 'media-text')
          component?.type && ['body-end', 'head-content', 'html-embed'].includes(component.type)
            ? undefined
            : component?.type
        }
        assetType={currentAssetType as any}
        onMediaUploaded={handleMediaUploaded}
      />
    </div>
  );
};
